import {
  BarChart3,
  Building2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  FileBarChart,
  FileSpreadsheet,
  FileText,
  FolderInput,
  Home,
  Layers,
  LogOut,
  Package,
  PackageOpen,
  Receipt,
  ScrollText,
  Search,
  Settings2,
  ShoppingCart,
  Store,
  Tag,
  Truck,
  UserCog,
  Users2,
  Warehouse,
} from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/AuthProvider";
import clsx from "clsx";

import type { LucideIcon } from "lucide-react";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  adminOnly?: boolean;
  section?: string;
};

const navItems: NavItem[] = [
  { to: "/", label: "Tổng quan", icon: Home, end: true, section: "Dashboard" },

  { to: "/doanh-thu", label: "Đơn hàng POScake", icon: ShoppingCart, section: "Bán hàng" },
  { to: "/doanh-thu-thu-cong", label: "Doanh thu thủ công", icon: Receipt, section: "Bán hàng" },
  { to: "/doi-chieu", label: "Đối chiếu ĐVVC", icon: ClipboardCheck, section: "Bán hàng" },
  { to: "/bang-ke-vtp", label: "Bảng kê ĐVVC", icon: Truck, section: "Bán hàng" },

  { to: "/hoa-don-dau-vao", label: "Hoá đơn đầu vào", icon: FileText, section: "Mua hàng" },
  { to: "/dong-bo-drive", label: "Đồng bộ Drive", icon: FolderInput, section: "Mua hàng" },
  { to: "/nha-cung-cap", label: "Nhà cung cấp", icon: Building2, section: "Mua hàng" },

  { to: "/san-pham", label: "Sản phẩm", icon: Package, section: "Kho & SP" },
  { to: "/nhom-san-pham", label: "Nhóm sản phẩm", icon: Layers, section: "Kho & SP" },
  { to: "/ma-hang", label: "Mã hàng", icon: Tag, section: "Kho & SP" },
  { to: "/theo-doi-kinh-doanh", label: "Theo dõi kinh doanh", icon: CircleDollarSign, section: "Kho & SP" },

  { to: "/so-s2a", label: "Sổ S2a-HKD", icon: ScrollText, section: "Kế toán" },
  { to: "/nguong-hoa-don", label: "Ngưỡng xuất HĐ", icon: FileBarChart, section: "Kế toán" },
  { to: "/kiem-tra-doanh-thu", label: "Kiểm tra DT", icon: Search, section: "Kế toán" },

  { to: "/thong-tin-kinh-doanh", label: "Thông tin KD", icon: Store, section: "Cấu hình" },
  { to: "/shops", label: "Cửa hàng POScake", icon: PackageOpen, section: "Cấu hình" },
  { to: "/tai-khoan-vtp", label: "Tài khoản VTP", icon: FileSpreadsheet, section: "Cấu hình" },
  { to: "/industries", label: "Ngành nghề", icon: Warehouse, section: "Cấu hình" },
  { to: "/users", label: "Người dùng", icon: Users2, adminOnly: true, section: "Cấu hình" },
];

const sectionOrder = ["Dashboard", "Bán hàng", "Mua hàng", "Kho & SP", "Kế toán", "Cấu hình"];

export function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const visible = navItems.filter((i) => !i.adminOnly || user?.role === "admin");
  const grouped = sectionOrder
    .map((s) => ({ section: s, items: visible.filter((i) => i.section === s) }))
    .filter((g) => g.items.length > 0);

  const currentItem = visible.find((i) =>
    i.end ? location.pathname === i.to : location.pathname === i.to || (i.to !== "/" && location.pathname.startsWith(i.to))
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        className={clsx(
          "bg-sidebar-bg text-sidebar-text border-r border-sidebar-border flex flex-col transition-all duration-200 sticky top-0 h-screen",
          collapsed ? "w-16" : "w-60"
        )}
      >
        {/* Logo */}
        <div className="px-4 py-4 border-b border-sidebar-border flex items-center justify-between">
          {!collapsed ? (
            <div>
              <div className="text-white font-semibold flex items-center gap-2">
                <div className="w-7 h-7 rounded bg-brand-600 flex items-center justify-center text-white text-xs font-bold">
                  NL
                </div>
                Nghi Long
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5 pl-9">Kế toán HKD</div>
            </div>
          ) : (
            <div className="w-7 h-7 rounded bg-brand-600 mx-auto flex items-center justify-center text-white text-xs font-bold">
              NL
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-4 scrollbar-hide">
          {grouped.map((g) => (
            <div key={g.section}>
              {!collapsed && (
                <div className="text-[10px] uppercase tracking-wider text-slate-500 px-3 py-1 font-medium">
                  {g.section}
                </div>
              )}
              <div className="space-y-0.5">
                {g.items.map((i) => (
                  <NavLink
                    key={i.to}
                    to={i.to}
                    end={i.end}
                    title={collapsed ? i.label : undefined}
                    className={({ isActive }) =>
                      clsx(
                        "flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors",
                        collapsed && "justify-center",
                        isActive
                          ? "bg-brand-600/20 text-white font-medium"
                          : "text-sidebar-text hover:bg-sidebar-bg-hover hover:text-white"
                      )
                    }
                  >
                    <i.icon size={16} className="shrink-0" />
                    {!collapsed && <span className="truncate">{i.label}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User + logout */}
        <div className="border-t border-sidebar-border p-2">
          {!collapsed ? (
            <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
              <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {(user?.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-white truncate">{user?.email}</div>
                <div className="text-[10px] text-slate-500 capitalize">{user?.role}</div>
              </div>
            </div>
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-500 mx-auto flex items-center justify-center text-white text-xs font-semibold mb-1">
              {(user?.email || "?")[0].toUpperCase()}
            </div>
          )}
          <button
            onClick={logout}
            title="Đăng xuất"
            className={clsx(
              "flex items-center gap-2 w-full px-3 py-1.5 text-sm rounded-md text-slate-300 hover:bg-red-600/20 hover:text-red-300 transition-colors",
              collapsed && "justify-center"
            )}
          >
            <LogOut size={16} className="shrink-0" />
            {!collapsed && <span>Đăng xuất</span>}
          </button>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={clsx(
              "flex items-center gap-2 w-full px-3 py-1.5 mt-1 text-sm rounded-md text-slate-500 hover:bg-sidebar-bg-hover hover:text-white transition-colors",
              collapsed && "justify-center"
            )}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            {!collapsed && <span className="text-xs">Thu gọn</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-10 h-14 bg-white border-b border-slate-200 px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <BarChart3 size={16} className="text-slate-400" />
            <span className="text-slate-400">Nghi Long</span>
            <span className="text-slate-300">/</span>
            <span className="font-medium text-slate-900">
              {currentItem?.label ?? "Trang"}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/thong-tin-kinh-doanh"
              className="text-slate-500 hover:text-slate-900 transition-colors"
              title="Cấu hình"
            >
              <Settings2 size={18} />
            </a>
            <a
              href="/users"
              className="text-slate-500 hover:text-slate-900 transition-colors"
              title="Người dùng"
            >
              <UserCog size={18} />
            </a>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
